import React, { useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
    CUSTOMER_TABVIEW_COLUMNS,
    NEWS_FEED,
    ROUTES,
    ROLES,
    SOURCE,
    isAdminRole,
    QUERY_KEYS,
} from '../../../constants';
import { getAccountByID, popup, deleteAccount, handleScroll } from '../../../utils';
import { ButtonComponent, DeleteConfirmationModal, EditButton } from '../../shared';
import {
    BlueArrow,
    Delete,
    DocumentRequest,
    EmptyDoc,
    IconHover1,
    ScoreCard,
} from '../../../assets/svg';
import { setSidePanel } from '../../../redux';
import { SelectPlatformModal } from './SelectPlatformModal';
import { formatPhoneToUSFormat } from '../../../utils/common/formatHelpers';
import useWindowSize from '../../../hooks/useWindowSize';
import { AccountMergeModal } from './AccountMergeModal';
import CircleSpinner from '../../../assets/svg/CircleSpinner';

const DEFAULT_ACCOUNT_INFO = {
    id: '',
    name: '',
    primary_users: [],
    phone: '',
    email: '',
    csm_id: '',
    csm_user_name: '',
    hiring_clients: [],
    tpas: [],
};

export const CustomerDetailsTab = ({ setTabOpen, tabData, setSelectedRowIndex }) => {
    const queryClient = useQueryClient();
    const scrollBar = useRef(null);
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const role = useSelector((s) => s.user.role);
    const showAction = useSelector((s) => s.user.isUserAdmin);
    const isContentManager = useSelector((state) => state.user.role === ROLES.CONTENT_MANAGER);
    const [openSelectPlatformModal, setOpenSelectPlatformModal] = useState(false);
    const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
    const [isAccountMergeModal, setIsAccountMergeModal] = useState(false);
    const [isScrolling, setIsScrolling] = useState(false);

    const { width } = useWindowSize();
    const isSmallScreen = width < 560;

    const handleScrolling = () => {
        handleScroll(setIsScrolling, scrollBar, 2000);
    };

    const {
        data: accountUserInfo,
        isFetching,
        isError,
        error,
    } = useQuery({
        queryKey: [QUERY_KEYS.ACCOUNT_BY_ID, tabData],
        queryFn: () => getAccountByID({ id: tabData }),
        select: (data) => ({
            id: data.data?.account?.account_id,
            name: data.data?.account?.name,
            email: data.data?.account?.email,
            source: data.data?.account?.source,
            phone: formatPhoneToUSFormat(data.data?.account?.phone),
            csm_id: data.data?.account?.csm_id,
            csm_user_name: data.data?.account?.csm_name,
            primary_users: data.data?.account?.users?.filter((user) => user.is_primary === 1),
            hiring_clients: data.data?.hiringClients,
            tpas: data.data?.platforms,
            scorecards: data.data?.scorecards,
        }),
        placeholderData: DEFAULT_ACCOUNT_INFO,
    });

    if (isError) popup('error', error?.message);

    const documentRequest = [];

    const deleteCustomerMutation = useMutation({
        mutationFn: async (selectedItemId) => await deleteAccount({ id: selectedItemId }),
        onSuccess: (res) => {
            queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.CUSTOMERS], exact: false });
            setTabOpen(false);
            setIsDeleteModalOpen(false);
            popup('error', res.message);
        },
        onError: (error) => {
            popup('error', error.message);
        },
    });

    const handleDeleteClick = ({ selectedItemId }) => {
        deleteCustomerMutation.mutate(selectedItemId);
    };

    return (
        <>
            <div className={'tab-open-area show' + `${isContentManager ? ' w-25 ' : ''}`}>
                {isFetching ? (
                    <CircleSpinner absolute size='3rem' />
                ) : (
                    <>
                        {openSelectPlatformModal && (
                            <SelectPlatformModal
                                openSelectPlatformModal={openSelectPlatformModal}
                                setOpenSelectPlatformModal={setOpenSelectPlatformModal}
                                customer={accountUserInfo}
                            />
                        )}
                        <div className='position-relative ps-3'>
                            {!openSelectPlatformModal && (
                                <button
                                    className='close-btn bg-white'
                                    onClick={() => {
                                        setTabOpen(false);
                                        dispatch(setSidePanel({ sidePanel: false, id: '' }));
                                        setSelectedRowIndex(0);
                                    }}
                                    type='button'
                                >
                                    <BlueArrow width={16} height={16} className={''} />
                                </button>
                            )}
                            <div
                                onScroll={handleScrolling}
                                className={`position-relative content-area px-4 py-3 ${isScrolling ? 'scrolling' : 'not-scrolling'}`}
                            >
                                <div className='row'>
                                    <div className='d-flex position-relative align-items-center edit-button-customer-tab'>
                                        <h6 className='mb-0 me-2'>Edit Client</h6>
                                        <div>
                                            {showAction && (
                                                <EditButton
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                    }}
                                                    navigateTo={ROUTES.ADMIN.CUSTOMER_EDIT}
                                                    id={accountUserInfo.id}
                                                />
                                            )}
                                        </div>
                                    </div>
                                    <div className='col-4 right-side'>
                                        <div className='d-flex align-items-start flex-column detail-area cursor-default'>
                                            {CUSTOMER_TABVIEW_COLUMNS.map((column, index) => {
                                                const value = accountUserInfo[column?.dataField];
                                                const isArray = Array.isArray(value);
                                                let content;
                                                if (isArray) {
                                                    content = value.length
                                                        ? value.map((data, idx) => (
                                                              <React.Fragment key={idx}>
                                                                  {column.customFormatter(data)}
                                                              </React.Fragment>
                                                          ))
                                                        : `No ${column.name} found`;
                                                } else if (column?.dataField === 'users') {
                                                    content = column.customFormatter({
                                                        data: accountUserInfo,
                                                    });
                                                } else {
                                                    content = column.customFormatter
                                                        ? column.customFormatter(value)
                                                        : value || '-';
                                                }
                                                return (
                                                    <div key={`tabview-${index}`}>
                                                        <p className={`title`}>
                                                            {column.name}
                                                            {isArray ? <span>{` (${value.length})`}</span> : null}
                                                        </p>
                                                        <div className='text d-flex flex-column  align-items-start'>
                                                            {content}
                                                        </div>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    </div>
                                    {!isContentManager && (
                                        <div className='col-8 left-side'>
                                            <div className='customer-area-btn d-flex flex-column flex-md-row align-items-end justify-content-end'>
                                                {isSmallScreen ? (
                                                    <span
                                                        id='action-btn'
                                                        title='Create Scorecard'
                                                        onClick={() => {
                                                            if (accountUserInfo.tpas.length) {
                                                                setOpenSelectPlatformModal(true);
                                                            } else {
                                                                popup(
                                                                    'error',
                                                                    'Platform not Available',
                                                                );
                                                            }
                                                        }}
                                                    >
                                                        <ScoreCard />
                                                    </span>
                                                ) : (
                                                    <ButtonComponent
                                                        className='w-auto ssc-secondary-white-btn'
                                                        text={'Create Scorecard'}
                                                        clickHandler={() => {
                                                            if (accountUserInfo.tpas.length) {
                                                                setOpenSelectPlatformModal(true);
                                                            } else {
                                                                popup(
                                                                    'error',
                                                                    'Platform not Available',
                                                                );
                                                            }
                                                        }}
                                                    ></ButtonComponent>
                                                )}
                                                {isSmallScreen ? (
                                                    <span
                                                        id='action-btn'
                                                        title='Request Documents'
                                                        onClick={() => {
                                                            return navigate(
                                                                ROUTES.ADMIN.DOCUMENT_REQUEST,
                                                                {
                                                                    state: {
                                                                        accountUserDetails:
                                                                            accountUserInfo,
                                                                    },
                                                                },
                                                            );
                                                        }}
                                                    >
                                                        <DocumentRequest />
                                                    </span>
                                                ) : (
                                                    <ButtonComponent
                                                        className='w-auto ssc-primary-green-btn'
                                                        text={'Request Documents'}
                                                        clickHandler={() => {
                                                            return navigate(
                                                                ROUTES.ADMIN.DOCUMENT_REQUEST,
                                                                {
                                                                    state: {
                                                                        accountUserDetails:
                                                                            accountUserInfo,
                                                                    },
                                                                },
                                                            );
                                                        }}
                                                    ></ButtonComponent>
                                                )}
                                                <button
                                                    type='button'
                                                    onClick={() => setIsDeleteModalOpen(true)}
                                                    id='action-btn-red'
                                                    title='Delete'
                                                >
                                                    <Delete
                                                        width={16}
                                                        height={16}
                                                        stroke='#ff004d'
                                                    />
                                                </button>
                                            </div>
                                            <p className='text-end sub-title'>
                                                {/* Recently Uploaded Documents */}
                                            </p>
                                            {/* for empty  document request*/}
                                            {!documentRequest.length ? (
                                                <div className='empty-doc d-flex align-items-end justify-content-end flex-column'>
                                                    <div className=''>
                                                        {width > 660 ? (
                                                            <EmptyDoc width={176} height={136} />
                                                        ) : null}
                                                    </div>
                                                    <div className='text-center'>
                                                        <span className='text-center text-line'>
                                                            {/*There are no document upload requests */}
                                                        </span>
                                                    </div>
                                                </div>
                                            ) : (
                                                <div className='filled-doc d-flex align-items-end justify-content-end'>
                                                    {documentRequest.map((doc) => {
                                                        return (
                                                            <div
                                                                key={doc.date}
                                                                className='d-flex flex-column align-items-start justify-content-start'
                                                            >
                                                                <span>
                                                                    {doc.date} <br />
                                                                    <span>{doc.time}</span>
                                                                </span>
                                                                <div className='doc-hov position-relative'>
                                                                    <div className=' position-relative'>
                                                                        <img
                                                                            src={doc.image}
                                                                            alt='doc'
                                                                            width='67'
                                                                            height='61'
                                                                            className='doc-img '
                                                                        />
                                                                    </div>
                                                                    <IconHover1
                                                                        className={'icon1'}
                                                                        height={16}
                                                                        width={16}
                                                                    />{' '}
                                                                    <IconHover1
                                                                        className={'icon1'}
                                                                        height={16}
                                                                        width={16}
                                                                    />
                                                                </div>
                                                            </div>
                                                        );
                                                    })}
                                                </div>
                                            )}

                                            <div className='news-feed-btn d-flex align-items-center justify-content-end'>
                                                <ButtonComponent
                                                    className='w-auto ssc-secondary-white-btn d-flex align-items-center'
                                                    text={NEWS_FEED.TITLE}
                                                    clickHandler={() => {
                                                        return navigate(
                                                            ROUTES.ADMIN.NEWS_FEED_ADMIN,
                                                            {
                                                                state: {
                                                                    accountUserDetails:
                                                                        accountUserInfo,
                                                                },
                                                            },
                                                        );
                                                    }}
                                                ></ButtonComponent>
                                            </div>
                                            <div className='news-feed-btn d-flex align-items-center justify-content-end'>
                                                <button
                                                    className='w-auto ssc-secondary-white-btn d-flex align-items-center'
                                                    aria-labelledby={'Account Review'}
                                                    onClick={() => {
                                                        return navigate(
                                                            ROUTES.ADMIN.ACCOUNT_REVIEWS,
                                                            {
                                                                state: {
                                                                    account_id: accountUserInfo?.id,
                                                                    name: accountUserInfo?.name,
                                                                },
                                                            },
                                                        );
                                                    }}
                                                >
                                                    Account Review
                                                </button>
                                            </div>
                                            {accountUserInfo.source === SOURCE.SCHEDULAR &&
                                                isAdminRole(role) && (
                                                    <div className='news-feed-btn d-flex align-items-center justify-content-end'>
                                                        <button
                                                            className='w-auto ssc-secondary-white-btn d-flex align-items-center'
                                                            aria-labelledby={'Merge Account'}
                                                            onClick={() =>
                                                                setIsAccountMergeModal(true)
                                                            }
                                                        >
                                                            Merge Account
                                                        </button>
                                                    </div>
                                                )}
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    </>
                )}
            </div>
            {isDeleteModalOpen && (
                <DeleteConfirmationModal
                    isDeleteModal={isDeleteModalOpen}
                    setIsDeleteModal={setIsDeleteModalOpen}
                    selectedItemId={tabData}
                    handleDeleteClick={handleDeleteClick}
                    deleteButtonName={'Client'}
                    isLoading={deleteCustomerMutation.isLoading}
                />
            )}
            {isAccountMergeModal && (
                <AccountMergeModal
                    accountUserInfo={accountUserInfo}
                    showModal={isAccountMergeModal}
                    setShowModal={setIsAccountMergeModal}
                />
            )}
        </>
    );
};
CustomerDetailsTab.propTypes = {
    tabData: PropTypes.string,
    setTabOpen: PropTypes.func,
    setSelectedRowIndex: PropTypes.func,
    debouncedApiCallFromParent: PropTypes.func,
};
